import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Star, Heart, Share2, Truck, Shield, RotateCcw, Minus, Plus,
  ShoppingCart, ChevronRight, Check
} from 'lucide-react';
import { useCart } from '../../context/CartContext';

const ProductDetail = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [isLiked, setIsLiked] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [selectedTab, setSelectedTab] = useState('description');

  const product = {
    id: parseInt(id) || 1,
    name: 'Ponni Boiled Rice',
    brand: 'Chozha',
    price: 16.66,
    originalPrice: 19.99,
    weight: '10kg',
    rating: 4.8,
    reviews: 234,
    stock: 45,
    tag: 'Best Seller',
    description: 'Premium quality Ponni Boiled Rice from Chozha. Sourced directly from the fertile fields of Thanjavur, Tamil Nadu. This rice is parboiled to perfection, retaining all essential nutrients while delivering that authentic South Indian taste. Perfect for everyday meals, biryanis, and festive occasions.',
    features: [
      'Premium Thanjavur origin',
      'Parboiled for better nutrition',
      'Perfect for daily cooking',
      'Aged for 12 months',
      'No artificial preservatives',
    ],
    images: [
      'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=800',
      'https://images.unsplash.com/photo-1627485937980-221c88ac04f9?w=800',
      'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=800',
    ],
  };

  const relatedProducts = [
    { id: 2, name: 'Idli Rice', brand: 'Chozha', price: 17.49, weight: '10kg', image: 'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400', rating: 4.9, reviews: 189 },
    { id: 3, name: 'Jeera Samba Rice', brand: 'Chozha', price: 16.65, weight: '5kg', image: 'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400', rating: 4.7, reviews: 156 },
    { id: 4, name: 'Sona Masoori Rice', brand: 'Chozha', price: 17.26, weight: '10kg', image: 'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400', rating: 4.6, reviews: 312 },
  ];

  const handleAddToCart = () => {
    addToCart({ ...product, quantity });
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-8 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
          <ChevronRight size={14} />
          <Link to="/shop" className="hover:text-primary-600 transition-colors">Shop</Link>
          <ChevronRight size={14} />
          <span className="text-gray-900 font-medium">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100 mb-4">
              <motion.img
                key={activeImage}
                src={product.images[activeImage]}
                alt={product.name}
                className="w-full aspect-square object-cover"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              />
            </div>
            <div className="flex gap-3">
              {product.images.map((img, index) => (
                <motion.button
                  key={index}
                  className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-colors ${
                    activeImage === index ? 'border-primary-500' : 'border-transparent'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveImage(index)}
                >
                  <img src={img} alt={`${product.name} ${index + 1}`} className="w-full h-full object-cover" />
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="flex items-center gap-3 mb-4">
              {product.tag && (
                <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-bold rounded-full">
                  {product.tag}
                </span>
              )}
              <span className="text-sm text-gray-500">{product.brand}</span>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{product.name}</h1>

            <div className="flex items-center gap-4 mb-6">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={20}
                    className={i < Math.floor(product.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
                  />
                ))}
              </div>
              <span className="text-sm text-gray-500">{product.rating} ({product.reviews} reviews)</span>
            </div>

            <div className="flex items-baseline gap-3 mb-6">
              <span className="text-4xl font-bold text-gray-900">£{product.price.toFixed(2)}</span>
              {product.originalPrice && (
                <span className="text-xl text-gray-400 line-through">£{product.originalPrice.toFixed(2)}</span>
              )}
              {product.originalPrice && (
                <span className="px-2 py-1 bg-red-100 text-red-600 text-sm font-bold rounded-lg">
                  Save {Math.round((1 - product.price / product.originalPrice) * 100)}%
                </span>
              )}
            </div>

            <p className="text-gray-600 mb-6 leading-relaxed">{product.description}</p>

            {/* Features */}
            <div className="space-y-2 mb-8">
              {product.features.map((feature, index) => (
                <motion.div
                  key={feature}
                  className="flex items-center gap-2 text-sm text-gray-600"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + index * 0.05 }}
                >
                  <Check size={16} className="text-green-500" />
                  {feature}
                </motion.div>
              ))}
            </div>

            {/* Quantity & Actions */}
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <div className="flex items-center bg-white border border-gray-200 rounded-xl">
                <motion.button
                  className="p-3 hover:text-primary-600 transition-colors"
                  whileTap={{ scale: 0.8 }}
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                >
                  <Minus size={18} />
                </motion.button>
                <span className="w-12 text-center font-bold text-lg">{quantity}</span>
                <motion.button
                  className="p-3 hover:text-primary-600 transition-colors"
                  whileTap={{ scale: 0.8 }}
                  onClick={() => setQuantity(quantity + 1)}
                >
                  <Plus size={18} />
                </motion.button>
              </div>

              <motion.button
                className="flex-1 min-w-[200px] py-4 bg-gradient-to-r from-primary-500 to-secondary-600 text-white rounded-xl font-bold text-lg shadow-lg flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02, boxShadow: '0 20px 40px -10px rgba(249, 115, 22, 0.3)' }}
                whileTap={{ scale: 0.98 }}
                onClick={handleAddToCart}
              >
                <ShoppingCart size={20} />
                Add to Cart — £{(product.price * quantity).toFixed(2)}
              </motion.button>

              <motion.button
                className={`p-4 rounded-xl border-2 transition-colors ${
                  isLiked ? 'bg-red-50 border-red-200 text-red-500' : 'border-gray-200 text-gray-400 hover:border-red-200 hover:text-red-500'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsLiked(!isLiked)}
              >
                <Heart size={24} className={isLiked ? 'fill-current' : ''} />
              </motion.button>

              <motion.button
                className="p-4 rounded-xl border-2 border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Share2 size={24} />
              </motion.button>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { icon: Truck, text: 'Free Delivery £30+' },
                { icon: Shield, text: 'Quality Guarantee' },
                { icon: RotateCcw, text: '7-Day Returns' },
              ].map((item) => (
                <div key={item.text} className="flex flex-col items-center text-center p-4 bg-white rounded-xl border border-gray-100">
                  <item.icon size={24} className="text-primary-500 mb-2" />
                  <span className="text-xs font-medium text-gray-600">{item.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="mt-16">
          <div className="flex gap-8 border-b border-gray-200 mb-8">
            {['description', 'reviews', 'shipping'].map((tab) => (
              <button
                key={tab}
                className={`pb-4 text-sm font-medium capitalize transition-colors relative ${
                  selectedTab === tab ? 'text-primary-600' : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setSelectedTab(tab)}
              >
                {tab}
                {selectedTab === tab && (
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500"
                    layoutId="tab-indicator"
                  />
                )}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-2xl p-8 border border-gray-100">
            {selectedTab === 'description' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <p className="text-gray-600 leading-relaxed">{product.description}</p>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {product.features.map((feature) => (
                    <div key={feature} className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                        <Check size={14} className="text-green-600" />
                      </div>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
            {selectedTab === 'reviews' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <p className="text-gray-500">Reviews coming soon...</p>
              </motion.div>
            )}
            {selectedTab === 'shipping' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <p className="text-gray-600 leading-relaxed">
                  We offer next-day delivery for orders placed before 5PM. Standard delivery takes 2-3 business days.
                  Free delivery on all orders over £30. We deliver across the UK including London, Birmingham, Manchester,
                  and surrounding areas.
                </p>
              </motion.div>
            )}
          </div>
        </div>

        {/* Related Products */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Related Products</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {relatedProducts.map((p, index) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={`/product/${p.id}`} className="block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow border border-gray-100 group">
                  <div className="aspect-square overflow-hidden">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <p className="text-xs text-gray-500 font-medium">{p.brand}</p>
                    <h3 className="font-bold text-gray-900 mt-1">{p.name}</h3>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xl font-bold text-gray-900">£{p.price.toFixed(2)}</span>
                      <span className="text-sm text-gray-500">{p.weight}</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
